import { useEffect, useState, useMemo } from "react";
import BarChart from "./BarChart";
import { CHART_BG_COLORS, CHART_BORDER_COLORS } from "../../utils/reports";
import {
  IdentifiedObjectItem,
  IdentifiedSpeciesCount,
} from "../../models/Media";
import { CameraItem } from "../../models/Camera";

interface SpeciesByYearOverMontProps {
  cameras: CameraItem[];
  selectedCameras: CameraItem[];
  allIdentifiedObjects: IdentifiedObjectItem[];
  selectedIdentifiedObjects: IdentifiedObjectItem[];
  reportData: IdentifiedSpeciesCount[];
  yearNames: string[];
}
export const getCount = (
  objectName: string,
  monthIndex: number,
  yearName: string,
  cameraId: string,
  reportLookup: Map<string, number>
): number => {
  const key = `${objectName}-${yearName}-${monthIndex}-${cameraId}`;
  return reportLookup.get(key) || 0;
};

export const getTotalCount = (
  objectName: string,
  monthIndex: number,
  yearName: string,
  cameras: CameraItem[],
  reportLookup: Map<string, number>
): number | null => {
  let total_count = 0;

  for (const camera of cameras) {
    const key = `${objectName}-${yearName}-${monthIndex}-${camera.camera_id}`;
    total_count += reportLookup.get(key) || 0;
  }

  return total_count > 0 ? total_count : null;
};

export const preprocessReportData = (reportData: IdentifiedSpeciesCount[]) => {
  const lookup = new Map();

  for (const data of reportData) {
    const date = new Date(data.date);
    const key = `${
      data.identified_object_name
    }-${date.getFullYear()}-${date.getMonth()}-${data.camera}`;
    lookup.set(key, (lookup.get(key) || 0) + 1);
  }

  return lookup;
};

const SpeciesOverMonthsByYear = ({
  cameras,
  selectedCameras,
  allIdentifiedObjects,
  selectedIdentifiedObjects,
  reportData,
  yearNames,
}: SpeciesByYearOverMontProps) => {
  const [csvData, setCsvData] = useState<any>();
  const ChartName = "Species Over Months by Year";

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Create lookup map for report data
  const reportLookup = useMemo(
    () => preprocessReportData(reportData),
    [reportData]
  );

  // Generate data for the chart
  const mapData = useMemo(() => {
    return {
      datasets: selectedIdentifiedObjects.flatMap((objectName, objIndex) =>
        yearNames.map((yearname, yearIndex) => ({
          label: objectName.name,
          data: monthNames.map((monthName, monthIndex) => ({
            month: monthName,
            object: {
              name: objectName.name,
              count:
                getTotalCount(
                  objectName.name,
                  monthIndex,
                  yearname.toString(),
                  cameras,
                  reportLookup
                ) || 0,
            },
          })),
          backgroundColor:
            CHART_BG_COLORS[Object.keys(CHART_BG_COLORS)[yearIndex]],
          borderColor:
            CHART_BORDER_COLORS[Object.keys(CHART_BORDER_COLORS)[yearIndex]],
          borderWidth: 1,
          parsing: {
            xAxisKey: "month",
            yAxisKey: "object.count",
          },
        }))
      ),
    };
  }, [selectedIdentifiedObjects, yearNames, monthNames, reportLookup]);

  // Generate CSV data
  useEffect(() => {
    const csv = selectedIdentifiedObjects.flatMap((objectName) =>
      yearNames.flatMap((yearname) =>
        selectedCameras.flatMap((camera) =>
          monthNames.map((monthName, monthIndex) => ({
            camera: camera.camera_id,
            Object: objectName.name,
            Year: yearname,
            Month: monthName,
            Count: getCount(
              objectName.name,
              monthIndex,
              yearname.toString(),
              camera.camera_id,
              reportLookup
            ),
          }))
        )
      )
    );

    setCsvData(csv);
  }, [selectedCameras, selectedIdentifiedObjects, yearNames, reportLookup]);

  const getYAxisConfig = (datasets: any[]) => {
    if (!datasets || datasets.length === 0)
      return { suggestedMax: 10, stepSize: 1 };

    const allValues = datasets.flatMap((dataset) =>
      dataset.data.map((d: any) => d?.object?.count || 0)
    );
    const maxValue = Math.max(...allValues, 10);

    let stepSize =
      maxValue < 10
        ? 1
        : maxValue < 70
        ? 10
        : maxValue < 350
        ? 50
        : maxValue < 1000
        ? 100
        : 200;
    return {
      suggestedMax: Math.ceil(maxValue / stepSize) * stepSize,
      stepSize,
    };
  };

  const { suggestedMax, stepSize } = mapData
    ? getYAxisConfig(mapData.datasets)
    : { suggestedMax: 10, stepSize: 1 };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          generateLabels: (chart: any) =>
            yearNames.flatMap((yearName) =>
              selectedIdentifiedObjects.map((object, index) => {
                const datasetIndex =
                  yearNames.length * index + yearNames.indexOf(yearName);
                const dataset = chart.data.datasets[datasetIndex];
                return {
                  text: `${yearName} - ${object.name}`,
                  fillStyle: dataset?.backgroundColor || "grey",
                  hidden: !chart.isDatasetVisible(datasetIndex),
                  lineWidth: dataset?.borderWidth || 1,
                  strokeStyle: dataset?.borderColor || "black",
                  datasetIndex,
                };
              })
            ),
        },
      },
      title: {
        display: true,
        text: "Animals by Year Over Month",
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: (context: any) =>
            `${context.dataset.label}: ${context.raw?.object?.count || 0}`,
          title: (context: any) => context[0]?.raw?.month || "",
        },
      },
    },
    layout: { padding: { left: 0, right: 10, top: 0, bottom: 0 } },
    scales: { y: { min: 0, suggestedMax, ticks: { stepSize } } },
  };

  return (
    <BarChart
      mapData={mapData}
      options={options}
      csvData={csvData}
      name={ChartName}
    />
  );
};

export default SpeciesOverMonthsByYear;
